import React, { useEffect } from 'react'

import { SmallLoader } from '@acorns/grove'
import { TogglesProvider, useTogglesStatus } from '@acorns/web-toggles-react'
import type { TogglesProviderConfig } from '@acorns/web-toggles-react'
import { ENV, useAnalyticsClient, useUserId } from '@acorns/web-utils'
import styled from 'styled-components'

import { getTogglesEnvIndex } from 'utils/toggles-utils'

const CenteringContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`

function CenteredLoader() {
  return (
    <CenteringContainer>
      <SmallLoader testId={'toggles-loader'} />
    </CenteringContainer>
  )
}

function LogTogglesInitializationError() {
  const analyticsClient = useAnalyticsClient()
  const { initializationError } = useTogglesStatus()

  useEffect(
    function logTogglesInitializationError() {
      if (analyticsClient && initializationError) {
        analyticsClient.track('Error Received', {
          object_name: 'toggles',
          error_title: 'Toggles Initialization Error',
          error_message:
            'Toggles failed to initialize. Error: ' + initializationError,
        })
      }
    },
    [analyticsClient, initializationError],
  )

  return null
}

export function WebAppTogglesProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const analyticsClient = useAnalyticsClient()
  const { userId } = useUserId()

  const authenticatedUserId = userId === 'no_user_id' ? undefined : userId

  const amplitudeDeploymentKeys =
    process.env.AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEYS!.split(',')

  const amplitudeDeploymentKeysIndex = getTogglesEnvIndex()

  const amplitudeDeploymentKey =
    amplitudeDeploymentKeys[amplitudeDeploymentKeysIndex]

  const togglesProviderConfig: TogglesProviderConfig = {
    sdkKey: amplitudeDeploymentKey,
    userAttributes: {
      userId: authenticatedUserId,
      deviceId: analyticsClient.getAnonymousId(),
    },
    waitForInitialization: true,
    waitForInitializationLoadingComponent: CenteredLoader,
    allowToggleOverrides: process.env.BUILD_ENV !== ENV.Production,
    amplitudeExperimentExposureTrackingProvider: {
      track(exposure) {
        if (exposure && exposure.variant) {
          analyticsClient.track('$exposure', {
            experiment_key: exposure.experiment_key ?? '',
            flag_key: exposure.flag_key,
            variant: exposure.variant,
          })
        }
      },
    },
    amplitudeExperimentFlagsServerUrl:
      process.env.AMPLITUDE_EXPERIMENT_FLAGS_SERVER_URL,
    amplitudeExperimentServerUrl: process.env.AMPLITUDE_EXPERIMENT_SERVER_URL,
    amplitudeExperimentUserProvider: {
      getUser() {
        return {
          user_id: authenticatedUserId,
          device_id: analyticsClient.getAnonymousId(),
        }
      },
    },
  }

  return (
    <TogglesProvider {...togglesProviderConfig}>
      <LogTogglesInitializationError />
      {children}
    </TogglesProvider>
  )
}
